import {
    Button,
    dispatchCustomEvent,
    Flexer,
    Heading,
    Icon,
    Popover,
    Portal,
    stopEvent,
    Text,
    useDialog,
    useDragEvent,
    useId,
    useLocalStorage,
    useStorage,
    useToast,
    useVisibility,
    View,
} from '@fold-dev/core'
import { useEffect, useMemo, useState } from 'react'
import { TbPlus } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { CACHE } from '../../constants'
import { byCreatedAt, getNewPositionForTargetIndex } from '../../helpers'
import { useProjects, useProjectUsers } from '../../hooks/api.hooks'
import { useAuth } from '../../hooks/auth.hook'
import { useAppStore } from '../../store/app'
import { SlidePanel } from '../slide-panel'
import { CollapseIcon } from './collapse-icon'
import { CreateConversation } from './create-conversation'
import { Project } from './project'

export const Conversations = (props) => {
    const { onAddProject } = props
    const { workspaceId } = useParams()
    const [panel, setPanel] = useState(0)
    const [list, setList] = useLocalStorage('FRONTEER_WORKSPACE_CONVERSATIONS', true)
    const { visible, show, hide } = useVisibility(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>('')
    const allProjects = useAppStore((state) => state.projects)
    const project = useAppStore((state) => state.project)
    const projects = useMemo(() => allProjects.filter((p) => p.isPrivate).sort(byCreatedAt), [allProjects])
    const teams = useAppStore((state) => state.teams)
    const thread = useAppStore((state) => state.thread)
    const actions = useAppStore((state) => state.actions)
    const sidebar = useAppStore((state) => state.sidebar)
    const projectsAPI = useProjects()
    const { setStorage, getStorage } = useStorage()
    const id = useId()
    const { setDialog, closeDialog } = useDialog()
    const { showToast } = useToast()
    const navigate = useNavigate()
    const { getUserId } = useAuth()
    const projectUsersAPI = useProjectUsers()

    const handleDrop = async (e) => {
        const { detail } = e
        const { origin, target } = detail
        const draggedProject = window[CACHE.NAV_PROJECT_DRAG_CACHE]

        if (target.group == 'conversations' && origin.group == 'conversations' && draggedProject) {
            const projectId = draggedProject.id
            const addProject = origin.areaId != id && target.areaId == id
            const moveProject = origin.areaId == id && target.areaId == id

            if (addProject) {
                const position = getNewPositionForTargetIndex(projects, target.index)

                actions.projectUpdate({
                    id: projectId,
                    position,
                    teamId: null,
                    teamColor: null,
                    teamName: null,
                    isClosed: false,
                })

                showToast({
                    anchor: 'bottom-left',
                    message: draggedProject.name + ' is now available to everyone in the workspace.',
                })

                window[CACHE.NAV_PROJECT_DRAG_CACHE] = null

                await projectsAPI.update(workspaceId, projectId, { position, isClosed: false, team: { id: null } })
                await projectUsersAPI.create(workspaceId, projectId, { users: [{ id: getUserId() }], sync: false })

                dispatchCustomEvent('project:refresh-preview', { id: projectId })
            }

            if (moveProject) {
                if (target.index == origin.index) return
                const position = getNewPositionForTargetIndex(projects, target.index)
                actions.projectUpdate({ id: projectId, position, team: null })
                window[CACHE.NAV_PROJECT_DRAG_CACHE] = null
                await projectsAPI.update(workspaceId, projectId, { position, team: { id: null } })
            }
        }
    }

    const handleNavigate = (project) => {
        actions.setMainLoading(true)
        actions.setMessages([])
        //actions.setSections([])
        //actions.setSections(getFastCache(getFastCacheKey(workspaceId, project.id)), false)

        if (project.isChannel || !!thread.id) {
            if (!!sidebar) actions.setSidebar('overview')
            actions.setThread({ id: null, messages: [] })
            dispatchCustomEvent('start-early-load-for-channel')
        }

        navigate(`/app/workspace/${workspaceId}/c/${project.id}/list`)
    }

    useDragEvent('ondrop', handleDrop)

    useEffect(() => {
        if (!visible) setPanel(0)
    }, [visible])

    if (!projects.length) return null

    return (
        <View
            width="100%"
            p="0rem 0.5rem"
            m="-3px 0 0.8rem 0">
            <View
                row
                gap={10}
                width="100%"
                position="relative"
                p="0.5rem 1rem 0rem 1.25rem"
                m={list ? '0' : '0 0 -0.5rem 0'}
                className="reveal-on-hover"
                color="var(--f-color-text-weaker)">
                <Heading
                    as="h6"
                    className="nav-section-heading f-ellipsis">
                    Direct Messages
                </Heading>

                <CollapseIcon
                    isOpen={list}
                    onClick={() => setList(!list)}
                />

                <Popover
                    arrow
                    focusTrap
                    width={250}
                    portal={Portal}
                    isVisible={visible}
                    onDismiss={hide}
                    content={
                        <div
                            onMouseDown={stopEvent}
                            onClick={stopEvent}>
                            <SlidePanel panel={panel}>
                                {/*     <Menu
                                    width="100%"
                                    border="none">
                                    <MenuHeading>Direct Messages</MenuHeading>
                                    <MenuItem
                                        prefix={<Icon icon={TbMessage} />}
                                        onClick={() => {
                                            setPanel(1)
                                        }}>
                                        Direct Message
                                    </MenuItem>
                                </Menu> */}
                                <CreateConversation
                                    noBack
                                    onCreate={({ users }) => {
                                        onAddProject({
                                            name: '',
                                            color: undefined,
                                            isChannel: true,
                                            isPrivate: true,
                                            isClosed: true,
                                            users,
                                        })
                                        setPanel(0)
                                    }}
                                    onBack={() => setPanel(0)}
                                />
                            </SlidePanel>
                        </div>
                    }>
                    <Button
                        subtle
                        width={25}
                        height={25}
                        className={visible ? undefined : 'reveal-on-hover-target'}
                        size="xs"
                        m="0 auto 0 -10px"
                        colorToken="text-weaker"
                        style={{ outlineWidth: 0 }}
                        onMouseDown={stopEvent}
                        onClick={show}>
                        <Icon icon={TbPlus} />
                    </Button>
                </Popover>

                <Flexer />
            </View>

            {list && (
                <View p="0.4rem 0 0 0">
                    {projects.map((project: any, index: number) => (
                        <View
                            key={index}
                            width="100%"
                            data-id={project.id}
                            onMouseDown={() => (window[CACHE.NAV_PROJECT_DRAG_CACHE] = project)}>
                            <Project
                                key={project.id}
                                id={project.id}
                                name={project.name}
                                icon={project.icon}
                                color={project.color}
                                totalTaskCount={project.totalTaskCount}
                                completeTaskCount={project.completeTaskCount}
                                isPrivate={project.isPrivate}
                                isChannel={project.isChannel}
                                isClosed={project.isClosed}
                                isShared={!project.isClosed}
                                joinable={project.joinable}
                                shortcode={project.shortcode}
                                projectUsers={project.projectUsers}
                                isMain={false}
                                excerpt=""
                                readonly={false}
                                muted={false}
                                archived={false}
                                onNavigate={() => handleNavigate(project)}
                            />
                        </View>
                    ))}
                </View>
            )}
        </View>
    )
}
